const RECONCILIATION_FAILURE_STATES = new Set([
  "error",
  "failed",
  "mismatch",
  "required",
]);

const cleanText = (value) => String(value || "").trim();

export function accountPositionRouteSymbol(symbol) {
  return cleanText(symbol)
    .replace(/^pos-/, "")
    .replace(/-(long|short)$/i, "")
    .replace(/:USDT$/i, "")
    .replace(/\//g, "");
}

export function accountPositionClosePath(accountId, symbol, side) {
  // FastAPI decodes %2F before route matching, so unified CCXT symbols such as
  // BTC/USDT:USDT must be converted to the route-safe raw exchange id.
  const routeSymbol = accountPositionRouteSymbol(symbol);
  const path = `/accounts/${encodeURIComponent(accountId)}/positions/${encodeURIComponent(routeSymbol)}/close`;
  return side ? `${path}?side=${encodeURIComponent(side)}` : path;
}

export function getAccountRuntimeTruth(status) {
  if (!status) {
    return {
      loaded: false,
      running: false,
      accountName: "Selected account",
      accountType: "account",
      executionLabel: "LOADING",
      executionTone: "pending",
      executionDetail: "Waiting for selected account status",
      reconciliationStatus: "unknown",
      reconciliationBlocked: false,
      reconciliationReason: "",
      riskLocked: false,
    };
  }

  const reconciliation = status.reconciliation || {};
  const reconciliationStatus = cleanText(reconciliation.status).toLowerCase() || "unknown";
  const isLiveMode = cleanText(status.mode).toLowerCase() === "live";
  const hasNoCredentials = status.live_available === false || reconciliationStatus === "no_credentials";
  const reconciliationBlocked = Boolean(
    !hasNoCredentials && (
      status.reconciliation_required
      || RECONCILIATION_FAILURE_STATES.has(reconciliationStatus)
      || (isLiveMode && reconciliationStatus === "not_checked")
    )
  );
  const riskLocked = Boolean(status.risk?.kill_switch || reconciliationBlocked);
  const running = status.running === true;
  const accountType = cleanText(status.exchange?.account_type || status.mode || "account").toLowerCase();
  const accountName = cleanText(status.exchange?.account_name || status.exchange?.account_id) || "Selected account";
  const reasons = Array.isArray(reconciliation.reasons)
    ? reconciliation.reasons.filter(Boolean).join(" · ")
    : "";
  const reconciliationReason = cleanText(
    hasNoCredentials && isLiveMode
      ? "Binance API keys not configured in Settings"
      : reconciliation.message
      || reconciliation.reason
      || reasons
      || (isLiveMode && reconciliationStatus === "not_checked"
        ? "Exchange reconciliation has not completed"
        : "")
      || (reconciliationBlocked ? "Exchange and local state must be reconciled" : ""),
  );

  const entryPaused = Boolean(status.risk?.entry_pause);
  const entryPauseReason = cleanText(status.risk?.entry_pause_reason || "");
  const consecutiveLosses = Number(status.risk?.consecutive_losses || 0);
  const lossStreakThreshold = Number(status.risk?.loss_streak_pause_count || 5);

  let executionLabel = "STOPPED";
  let executionTone = "stopped";
  let executionDetail = `${accountType} account is not actively trading`;

  if (hasNoCredentials && isLiveMode) {
    executionLabel = "NO KEYS";
    executionTone = "warning";
    executionDetail = "Binance API keys not configured in Settings";
  } else if (reconciliationStatus === "error" || reconciliationStatus === "failed") {
    executionLabel = "RECON ERROR";
    executionTone = "danger";
    executionDetail = reconciliationReason || "Reconciliation failed";
  } else if (isLiveMode && reconciliationStatus === "not_checked") {
    executionLabel = "NOT CHECKED";
    executionTone = "danger";
    executionDetail = reconciliationReason;
  } else if (reconciliationBlocked) {
    executionLabel = "RECONCILE";
    executionTone = "danger";
    executionDetail = reconciliationReason;
  } else if (status.risk?.kill_switch) {
    executionLabel = "HALTED";
    executionTone = "danger";
    executionDetail = cleanText(status.risk?.lock_reason || status.risk?.kill_switch_reason) || "Risk guard has stopped trading";
  } else if (running && entryPaused) {
    executionLabel = "PAUSED";
    executionTone = "warning";
    executionDetail = entryPauseReason || "Trade entries paused by Risk Guard";
  } else if (running) {
    executionLabel = "RUNNING";
    executionTone = "running";
    executionDetail = `${accountType} account is actively trading`;
  }

  return {
    loaded: true,
    running,
    accountName,
    accountType,
    executionLabel,
    executionTone,
    executionDetail,
    reconciliationStatus,
    reconciliationBlocked,
    reconciliationReason,
    riskLocked,
    entryPaused,
    entryPauseReason,
    consecutiveLosses,
    lossStreakThreshold,
  };
}
